import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Copy, Check, ShieldCheck, Globe, Trash2, Radio, Info } from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

const UNCHANGED_KEY = "__UNCHANGED__";
const MASK_DISPLAY = "••••••••••••••••••••";

const ZendeskConfig = ({ activeAiAgentId }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [zendeskConfig, setZendeskConfig] = useState({
    subdomain: "",
    email: "",
    api_token: "",
    is_active: false,
    streaming_enabled: false,
  });
  const [webhookUrl, setWebhookUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const response = await ApiClient.request("/zendesk/config", {
        aiAgentId: activeAiAgentId,
      });
      if (response.success && response.config) {
        setZendeskConfig({
          subdomain: response.config.subdomain || "",
          email: response.config.email || "",
          api_token: response.config.api_token ? UNCHANGED_KEY : "",
          is_active: response.config.is_active || false,
          streaming_enabled: response.config.streaming_enabled || false,
        });
        setWebhookUrl(response.config.webhook_url || "");
      }
    } catch (error) {
      console.error("Failed to load Zendesk config:", error);
    }
  }, [activeAiAgentId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const saveConfig = async () => {
    if (!zendeskConfig.subdomain.trim()) {
      showToast("Please enter your Zendesk subdomain", "error");
      return;
    }

    if (!zendeskConfig.email.trim()) {
      showToast("Please enter your Zendesk agent email", "error");
      return;
    }

    if (!zendeskConfig.api_token.trim()) {
      showToast("Please enter your Zendesk API token", "error");
      return;
    }

    try {
      setSaving(true);
      const response = await ApiClient.request("/zendesk/config", {
        method: "PUT",
        body: JSON.stringify({
          ...zendeskConfig,
          api_token: zendeskConfig.api_token === UNCHANGED_KEY ? undefined : zendeskConfig.api_token,
        }),
        aiAgentId: activeAiAgentId,
      });

      if (response.success) {
        setWebhookUrl(response.webhook_url);
        showToast("Zendesk configuration saved successfully!", "success");
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    } finally {
      setSaving(false);
    }
  };

  const deleteConfig = async () => {
    try {
      setSaving(true);
      const response = await ApiClient.request("/ai-agents/integrations/zendesk", {
        method: "DELETE",
      });
      if (response.success) {
        setZendeskConfig({
          subdomain: "",
          email: "",
          api_token: "",
          is_active: false,
          streaming_enabled: false,
        });
        setWebhookUrl("");
        showToast("Zendesk integration deleted successfully!", "success");
      }
    } catch (error) {
      // handled globally
    } finally {
      setSaving(false);
      setIsDeleteModalOpen(false);
    }
  };

  const getDisplayWebhookUrl = () => {
    if (!webhookUrl) return "";
    let path = webhookUrl;
    if (webhookUrl.startsWith("http://") || webhookUrl.startsWith("https://")) {
      try {
        const urlObj = new URL(webhookUrl);
        path = urlObj.pathname + urlObj.search;
      } catch (e) {}
    }
    const baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;
    let cleanBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    let cleanPath = path.startsWith("/") ? path : "/" + path;
    if (cleanBase.endsWith("/api") && cleanPath.startsWith("/api")) {
      cleanBase = cleanBase.slice(0, -4);
    }
    return cleanBase + cleanPath;
  };

  const displayUrl = getDisplayWebhookUrl();

  const copyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(displayUrl);
      setCopied(true);
      showToast("Webhook URL copied to clipboard!", "success");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      showToast("Failed to copy URL", "error");
    }
  };

  const toggleActive = async () => {
    if (!zendeskConfig.is_active) {
      if (!zendeskConfig.subdomain?.trim() || !zendeskConfig.email?.trim() || !zendeskConfig.api_token?.trim()) {
        showToast("Please enter and save subdomain, email, and API token first", "error");
        return;
      }
      if (zendeskConfig.api_token !== UNCHANGED_KEY) {
        showToast("Please save your configuration changes before activating", "warning");
        return;
      }
    }

    try {
      const newActiveState = !zendeskConfig.is_active;
      const response = await ApiClient.request("/zendesk/config", {
        method: "PUT",
        body: JSON.stringify({ is_active: newActiveState }),
        aiAgentId: activeAiAgentId,
      });

      if (response.success) {
        setZendeskConfig((prev) => ({ ...prev, is_active: newActiveState }));
        showToast(newActiveState ? "Zendesk integration activated!" : "Zendesk integration deactivated", "success");
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    }
  };

  const toggleStreaming = async () => {
    try {
      const newStreamingState = !zendeskConfig.streaming_enabled;
      const response = await ApiClient.request("/zendesk/config", {
        method: "PUT",
        body: JSON.stringify({ streaming_enabled: newStreamingState }),
        aiAgentId: activeAiAgentId,
      });

      if (response.success) {
        setZendeskConfig((prev) => ({ ...prev, streaming_enabled: newStreamingState }));
        showToast("Streaming status updated", "success");
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8 space-y-4 sm:space-y-6">
        
        {/* Top Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 sm:pb-5 border-b border-zinc-100 gap-3 sm:gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/integrations")}
              className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors"
              title="Back to Integrations"
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">Zendesk Integration</h1>
              <p className="text-xs text-zinc-500 mt-0.5">
                Sync agent intelligence directly with your Zendesk Support ticket workflows.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 self-start sm:self-auto pl-9 sm:pl-0">
            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide uppercase border ${
              zendeskConfig.is_active
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-zinc-50 text-zinc-500 border-zinc-200"
            }`}>
              {zendeskConfig.is_active ? "Active" : "Disabled"}
            </span>
            <button
              type="button"
              onClick={toggleActive}
              className={`w-8 h-4.5 rounded-full transition-colors relative flex-shrink-0 ${
                zendeskConfig.is_active ? "bg-zinc-900" : "bg-zinc-200"
              }`}
              title={zendeskConfig.is_active ? "Deactivate integration" : "Activate integration"}
            >
              <div
                className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full transition-transform ${
                  zendeskConfig.is_active ? "left-4" : "left-0.5"
                }`}
              />
            </button>
          </div>
        </div>

        {/* Credentials Card */}
        <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-6 space-y-5">
          <div className="flex items-center gap-2.5 pb-3 border-b border-zinc-100">
            <ShieldCheck className="text-zinc-800 shrink-0" size={18} />
            <div>
              <h2 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">Zendesk API Credentials</h2>
              <p className="text-[11px] text-zinc-500">Provide your Zendesk subdomain, admin email, and API token</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Subdomain */}
            <div>
              <label className="block text-xs font-medium text-zinc-700 mb-1.5">
                Zendesk Subdomain
              </label>
              <div className="flex items-center rounded-lg border border-zinc-200 bg-zinc-50/50 focus-within:bg-white focus-within:border-zinc-900 transition-colors overflow-hidden">
                <input
                  type="text"
                  value={zendeskConfig.subdomain}
                  onChange={(e) =>
                    setZendeskConfig({
                      ...zendeskConfig,
                      subdomain: e.target.value,
                    })
                  }
                  placeholder="your-subdomain"
                  className="flex-1 px-3.5 py-2 text-xs bg-transparent border-0 text-zinc-900 placeholder:text-zinc-400 font-mono focus:outline-none"
                />
                <span className="px-3 py-2 text-xs text-zinc-500 bg-zinc-100/80 border-l border-zinc-200 select-none">
                  .zendesk.com
                </span>
              </div>
              <p className="text-[11px] text-zinc-400 mt-1">
                If your URL is <code className="text-zinc-600 bg-zinc-100 px-1 py-0.5 rounded text-[10px]">https://company.zendesk.com</code>, enter <code className="text-zinc-600 bg-zinc-100 px-1 py-0.5 rounded text-[10px]">company</code>.
              </p>
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs font-medium text-zinc-700 mb-1.5">
                Admin / Agent Email
              </label>
              <input
                type="email"
                value={zendeskConfig.email}
                onChange={(e) =>
                  setZendeskConfig({
                    ...zendeskConfig,
                    email: e.target.value,
                  })
                }
                placeholder="agent@company.com"
                className="w-full px-3.5 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:border-zinc-900 text-zinc-900 transition-colors"
              />
              <p className="text-[11px] text-zinc-400 mt-1">
                The Zendesk user account email address associated with the API Token.
              </p>
            </div>

            {/* API Token */}
            <div>
              <label className="block text-xs font-medium text-zinc-700 mb-1.5">
                API Token
              </label>
              <input
                type="password"
                value={zendeskConfig.api_token === UNCHANGED_KEY ? MASK_DISPLAY : zendeskConfig.api_token}
                onChange={(e) =>
                  setZendeskConfig({
                    ...zendeskConfig,
                    api_token: e.target.value,
                  })
                }
                onFocus={() => {
                  if (zendeskConfig.api_token === UNCHANGED_KEY) {
                    setZendeskConfig({ ...zendeskConfig, api_token: "" });
                  }
                }}
                placeholder={zendeskConfig.api_token === UNCHANGED_KEY ? MASK_DISPLAY : "Your Zendesk API Token"}
                className="w-full px-3.5 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:border-zinc-900 text-zinc-900 font-mono transition-colors"
              />
              <p className="text-[11px] text-zinc-400 mt-1">
                Generate in <span className="text-zinc-600 font-medium">Admin Center → Apps and Integrations → APIs → Zendesk API</span> (enable Token Access).
              </p>
            </div>

            {/* Streaming Toggle */}
            <div className="flex items-center justify-between p-3.5 bg-zinc-50 rounded-lg border border-zinc-200">
              <div className="space-y-0.5 pr-3">
                <div className="flex items-center gap-1.5">
                  <Radio size={13} className={zendeskConfig.streaming_enabled ? "text-zinc-900" : "text-zinc-400"} />
                  <h4 className="text-xs font-medium text-zinc-900">Streaming Responses</h4>
                </div>
                <p className="text-[11px] text-zinc-500">
                  Stream response tokens to Zendesk tickets as comments are drafted.
                </p>
              </div>
              <button
                type="button"
                onClick={toggleStreaming}
                className={`w-8 h-4.5 rounded-full transition-colors relative flex-shrink-0 ${
                  zendeskConfig.streaming_enabled ? "bg-zinc-900" : "bg-zinc-200"
                }`}
                title={zendeskConfig.streaming_enabled ? "Disable Streaming" : "Enable Streaming"}
              >
                <div
                  className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full transition-transform ${
                    zendeskConfig.streaming_enabled ? "left-4" : "left-0.5"
                  }`}
                />
              </button>
            </div>
          </div>

          <div className="pt-3 border-t border-zinc-100 flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-2.5">
            {zendeskConfig.subdomain ? (
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(true)}
                disabled={saving}
                className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-40"
              >
                <Trash2 size={14} />
                <span>Disconnect Zendesk</span>
              </button>
            ) : <div />}

            <button
              type="button"
              onClick={saveConfig}
              disabled={saving || !zendeskConfig.subdomain?.trim() || !zendeskConfig.email?.trim() || !zendeskConfig.api_token?.trim()}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 disabled:opacity-40 text-xs font-medium transition-colors"
            >
              {saving ? (
                <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Save size={14} />
              )}
              <span>{saving ? "Saving..." : "Save Configuration"}</span>
            </button>
          </div>
        </div>

        {/* Webhook Endpoint Card */}
        {webhookUrl && (
          <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-6 space-y-3">
            <div className="flex items-center gap-2.5 pb-3 border-b border-zinc-100">
              <Globe className="text-zinc-800 shrink-0" size={18} />
              <div>
                <h3 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">Live Webhook Endpoint</h3>
                <p className="text-[11px] text-zinc-500">Receive real-time ticket creation and update triggers from Zendesk</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 p-1.5 sm:p-2 bg-zinc-50 rounded-lg border border-zinc-200">
              <div className="font-mono text-[11px] sm:text-xs text-zinc-700 px-2 py-1 flex-1 break-all select-all min-w-0">
                {displayUrl}
              </div>
              <button
                type="button"
                onClick={copyWebhookUrl}
                className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex-shrink-0 ${
                  copied
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-white text-zinc-800 border border-zinc-200 hover:bg-zinc-100"
                }`}
                title={copied ? "Copied!" : "Copy Webhook URL"}
              >
                {copied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
                <span>{copied ? "Copied" : "Copy URL"}</span>
              </button>
            </div>

            <div className="p-3.5 bg-zinc-50 rounded-lg border border-zinc-200 text-xs text-zinc-600 space-y-2">
              <div className="flex items-center gap-1.5 font-medium text-zinc-800">
                <Info size={13} className="text-zinc-500" />
                <span>Zendesk Webhook Setup Guide:</span>
              </div>
              <ol className="list-decimal pl-4 space-y-1 text-[11px] text-zinc-500">
                <li>In Zendesk Admin Center, go to <span className="text-zinc-700 font-medium">Apps and Integrations → Webhooks</span> and create a Trigger webhook with this URL.</li>
                <li>Set Method to <strong className="text-zinc-700">POST</strong> and Format to <strong className="text-zinc-700">JSON</strong>.</li>
                <li>Go to <span className="text-zinc-700 font-medium">Objects and Rules → Triggers</span> and notify this webhook using this JSON payload:</li>
              </ol>
              <pre className="p-2.5 bg-zinc-900 text-zinc-100 font-mono text-[11px] rounded-md overflow-x-auto">
{`{
  "ticket_id": "{{ticket.id}}",
  "latest_comment": "{{ticket.latest_comment}}",
  "comment_author_id": "{{ticket.latest_comment.author.id}}"
}`}
              </pre>
            </div>
          </div>
        )}
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={deleteConfig}
        title="Disconnect Zendesk?"
        message="Are you sure you want to disconnect this integration? Ticket webhooks and AI ticket sync will be stopped."
        confirmText="Disconnect"
        isLoading={saving}
      />
    </div>
  );
};

export default ZendeskConfig;
